import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { FirebaseService } from './firebase.service';
import { StorageKey, StorageService } from './storage.service';
import { Rating } from '../types/rating';

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  readonly showFeedbackModal = new BehaviorSubject(false);

  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService,
    private storageService: StorageService,
  ) {}

  public sendFeedback(feedback: string, userDataCsv: string, gameOverride?: string): Observable<string> {
    const formData = new FormData();
    formData.append('game', gameOverride ?? environment.gameName);
    formData.append('feedback', feedback);
    formData.append('device', navigator.userAgent);
    formData.append('user_data_csv', String(userDataCsv));
    return this.http.post<string>('https://feedback.gametailors.com/feedback/send_mail.php', formData);
  }

  public async rateMission(missionId: number, rating: number) {
    const ratingObj: Rating = {
      mission: missionId,
      rating: rating,
      timestamp: new Date().toISOString(),
    };

    const uuid = this.storageService.get(StorageKey.UUID);
    if (!uuid) throw new Error('uid not found');

    const data = await this.firebaseService.readRealtimeDatabase<Rating[]>('ratings/' + uuid);

    const newData = data ? [...data.filter((d) => d.mission !== missionId), ratingObj] : [ratingObj];
    await this.firebaseService.setRealtimeDatabase('ratings/' + uuid, newData);
  }

  public async getRatings() {
    try {
      const uuid = this.storageService.get(StorageKey.UUID);
      if (!uuid) throw new Error('uid not found');
      return await this.firebaseService.readRealtimeDatabase<Rating[]>('ratings/' + uuid);
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  public async getMissionRating(missionId: number): Promise<Rating | null> {
    try {
      const ratings = await this.getRatings();
      return ratings.filter((r) => r.mission === missionId)?.[0] ?? null;
    } catch {
      return null;
    }
  }

  public async sendTextFeedback(text: string) {
    const uuid = this.storageService.get(StorageKey.UUID);
    if (!uuid) throw new Error('uid not found');

    const newFeedback = {
      text: text,
      timestamp: new Date().toISOString(),
    };

    const currentData = await this.firebaseService.readRealtimeDatabase<unknown[]>('feedback/' + uuid);
    const newData = currentData ? [...currentData, newFeedback] : [newFeedback];

    await this.firebaseService.setRealtimeDatabase('feedback/' + uuid, newData);
  }
}
